import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FONT } from "@shared/brand";

/**
 * SMS MFA login interstitial (s90). Reached after a password login when MFA is enabled.
 * The mfaToken + phoneLastFour are passed via sessionStorage by Login.tsx (survives a
 * refresh). Accessible unauthenticated — no session exists until the code is verified.
 */
export default function VerifySms() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const mfaToken = (() => { try { return sessionStorage.getItem("mfaToken") ?? ""; } catch { return ""; } })();
  const phoneLastFour = (() => { try { return sessionStorage.getItem("mfaPhoneLastFour") ?? ""; } catch { return ""; } })();

  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // No token (direct nav / cleared storage) → back to sign in.
  useEffect(() => {
    if (!mfaToken) setLocation("/login");
  }, [mfaToken, setLocation]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const bounceToLogin = (title: string) => {
    try { sessionStorage.removeItem("mfaToken"); sessionStorage.removeItem("mfaPhoneLastFour"); } catch {}
    toast({ title, variant: "destructive" });
    setLocation("/login");
  };

  const handleVerify = async () => {
    if (code.length !== 6) { setError("Enter the 6-digit code."); return; }
    setVerifying(true);
    setError("");
    try {
      const res = await fetch("/api/auth/verify-mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaToken, code }),
        credentials: "include",
      });
      if (res.ok) {
        try { sessionStorage.removeItem("mfaToken"); sessionStorage.removeItem("mfaPhoneLastFour"); } catch {}
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        toast({ title: "Welcome back!", description: "You have successfully logged in." });
        setLocation("/dashboard");
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (body.error === "invalid_code") {
        setError(`Incorrect code. ${body.attemptsRemaining} attempt${body.attemptsRemaining === 1 ? "" : "s"} remaining.`);
      } else if (body.error === "max_attempts") {
        bounceToLogin("Too many incorrect attempts. Please sign in again.");
      } else if (body.error === "session_expired") {
        bounceToLogin("Session expired. Please sign in again.");
      } else {
        setError(body.message || body.error || "Verification failed.");
      }
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setError("");
    try {
      const res = await fetch("/api/auth/resend-mfa-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaToken }),
        credentials: "include",
      });
      if (res.ok) {
        setCooldown(60);
        toast({ title: "Code sent", description: "Check your phone for a new code." });
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (body.error === "session_expired") {
        bounceToLogin("Session expired. Please sign in again.");
      } else {
        toast({ title: "Could not resend", description: body.error || "Please try again.", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Could not resend", description: e.message || "Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <AuthBrandHeader />
        <Card className="w-full">
          <CardHeader>
            <CardTitle style={{ fontFamily: FONT.serif }}>Enter verification code</CardTitle>
            <CardDescription data-testid="text-sms-description">
              We sent a 6-digit code to the number ending in <strong>{phoneLastFour || "••••"}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleVerify(); }}
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              autoComplete="one-time-code"
              data-testid="input-mfa-code"
              className="text-center text-lg tracking-[0.4em]"
            />
            {error && (
              <p className="text-sm" style={{ color: "#C53030" }} data-testid="text-mfa-error">{error}</p>
            )}
            <Button
              type="button"
              className="w-full"
              onClick={handleVerify}
              disabled={verifying || code.length !== 6}
              data-testid="button-verify-mfa"
            >
              {verifying ? "Verifying..." : "Verify"}
            </Button>
            <div className="text-sm text-muted-foreground">
              Didn't receive it?{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0}
                className="text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                data-testid="button-resend-mfa"
              >
                {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
              </button>
            </div>
            <div className="text-center text-sm">
              <Link href="/login">
                <span className="text-primary hover:underline cursor-pointer" data-testid="link-back-to-login">
                  Back to sign in
                </span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
